import { Avatar, DatePicker, theme, TimePicker } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import { motion } from "framer-motion";
import {
  ArrowUpDown,
  CheckCircle2Icon,
  Edit2,
  Eye,
  Loader2,
  Trash2,
  XCircleIcon,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "src/components/ui/sheet";
import { DataTable } from "../components/DataTable";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { BaseUrlApi, ErrorMessage, UrlImage } from "../lib/api";
import { useAPI } from "./../context/APIContext";
import SkeletonTable from "./SkeletonTable";
import { useTheme } from "next-themes";
import { cn, formatDate, formatTime, formatTimeNow } from "../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import { DialogDelete } from "../components/Common/DialogDelete";

export default function Attendance() {
  const { user, loading } = useAuth();
  const { CheckAttendanceItems } = useAPI();
  // ------------------------------
  const { checkAttendance, loadingCheckAttendance, getCheckAttendance } =
    CheckAttendanceItems;
  const [loadingAttendanceStatus, setLoadingAttendanceStatus] = useState(false);
  const [loadingDeparture, setLoadingDeparture] = useState(false);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [departureAttendance, setDepartureAttendance] = useState([]);
  const [timeWork, setTimeWork] = useState(null);
  const [showAttendanceSheet, setShowAttendanceSheet] = useState(false);
  const [lateReason, setLateReason] = useState("");
  const [showDepartureSheet, setShowDepartureSheet] = useState(false);
  const [earlyReason, setEarlyReason] = useState("");
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(false);

  const fetchTimeWork = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/attendance/timeWork`);
      setTimeWork(data.data);
    } catch (error) {
      console.error("Error fetching time work:", ErrorMessage(error));
    }
  };

  const ApiUrl =
    !loading && user?.role === "admin" ? `/attendance/getAll` : `/attendance`;

  const handleFetchAttendance = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}${ApiUrl}`);
      setDepartureAttendance(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object") toast("Error", { description: result });
    } finally {
      setLoadingAttendance(false);
    }
  };

  useEffect(() => {
    setLoadingAttendance(true);
    fetchTimeWork();
    handleFetchAttendance();
  }, []);

  const submitAttendance = async (reason) => {
    try {
      setLoadingAttendanceStatus(true);
      await axios.post(`${BaseUrlApi}/attendance/create`, {
        reason_late: reason,
      });
      toast("أهلا بك في أساكيب", { description: "تم أخذ الحضور بنجاح 🎉" });
      handleFetchAttendance();
      getCheckAttendance();
    } catch (error) {
      toast("Something Wrong!", { description: ErrorMessage(error) });
    } finally {
      setLoadingAttendanceStatus(false);
      setShowAttendanceSheet(false);
      setLateReason("");
    }
  };

  const submitDeparture = async (reason) => {
    try {
      setLoadingDeparture(true);
      await axios.post(`${BaseUrlApi}/attendance/createDeparture`, {
        reason_early: reason,
      });
      toast("رافقتك السلامة 👋", { description: "تم أخذ الانصراف بنجاح 🎉" });
      handleFetchAttendance();
      getCheckAttendance();
    } catch (error) {
      toast("حدث خطأ", { description: ErrorMessage(error) });
    } finally {
      setLoadingDeparture(false);
      setShowDepartureSheet(false);
      setEarlyReason("");
    }
  };

  const handleEditReasons = async () => {
    setLoadingEdit(true);
    try {
      await axios.post(`${BaseUrlApi}/attendance/${editRecord.id}`, {
        reason_late: editRecord.reason_late,
        reason_early: editRecord.reason_early,
      });
      toast("تم التعديل بنجاح");
      setEditSheetOpen(false);
      setEditRecord(null);
      handleFetchAttendance();
      getCheckAttendance();
    } catch (error) {
      toast("حدث خطأ", { description: ErrorMessage(error) });
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleAttendanceClick = async (e) => {
    e.preventDefault();
    if (!timeWork) {
      toast("تنبيه", { description: "لم يتم جلب وقت العمل الرسمي بعد" });
      return;
    }
    const currentHour = formatTimeNow();
    const officialHourIn = timeWork?.time_in;
    if (currentHour <= officialHourIn) {
      await submitAttendance("");
    } else {
      setShowAttendanceSheet(true);
    }
  };

  const handleDepartureClick = async (e) => {
    e.preventDefault();
    if (!timeWork) {
      toast("تنبيه", { description: "لم يتم جلب وقت العمل الرسمي بعد" });
      return;
    }
    const currentHour = formatTimeNow();
    const officialHourOut = timeWork?.time_out;
    if (currentHour >= officialHourOut) {
      await submitDeparture("");
    } else {
      setShowDepartureSheet(true);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "حاضر":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle2Icon className="ml-1 h-3 w-3" /> حاضر
          </Badge>
        );
      case "متأخر":
        return (
          <Badge
            variant="outline"
            className="bg-amber-100 text-amber-800 hover:bg-amber-100"
          >
            متأخر
          </Badge>
        );
      case "مبكر":
        return (
          <Badge
            variant="outline"
            className="bg-blue-100 text-blue-800 hover:bg-blue-100"
          >
            مبكر
          </Badge>
        );
      case "غائب":
        return (
          <Badge
            variant="outline"
            className="bg-red-100 text-red-800 hover:bg-red-100"
          >
            <XCircleIcon className="ml-1 h-3 w-3" /> غائب
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  function timeStringToSeconds(timeStr) {
    if (!timeStr) return 0;
    const [hours, minutes, seconds] = timeStr.split(":").map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  }

  function secondsToTimeString(totalSeconds) {
    const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
    const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
      2,
      "0"
    );
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  }

  const columns = [
    {
      accessorKey: `user_id.name`,
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          الموظف
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Avatar
            className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium",
              "bg-blue-100 text-blue-600",
              "dark:bg-blue-900 dark:text-blue-300"
            )}
            src={`${UrlImage}${row.original.user_id.image}`}
          >
            {row.original.user_id.name.charAt(0)}
          </Avatar>
          <div className="text-right">
            <div className="font-medium">{row.original.user_id.name}</div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "date",
      header: "التاريخ",
      cell: ({ row }) =>
        row.original.created_at ? formatDate(row.original.created_at) : "___",
    },
    {
      accessorKey: "time_in",
      header: "الحضور",
      cell: ({ row }) =>
        row.original.time_in ? formatTime(row.original.time_in) : "___",
    },
    {
      accessorKey: "time_out",
      header: "الانصراف",
      cell: ({ row }) =>
        row.original.time_out ? formatTime(row.original.time_out) : "___",
    },
    {
      accessorKey: "Hours",
      header: "ساعات الحضور",
      cell: ({ row }) => row.original.Hours || "___",
    },
    {
      accessorKey: "Hours_Late",
      header: "مدة التأخر",
      cell: ({ row }) => {
        const diffInSeconds =
          timeStringToSeconds(user?.work_hours) -
          timeStringToSeconds(row.original.Hours);
        const resultTime = secondsToTimeString(diffInSeconds);
        return user?.work_hours !== "00:00:00" && row.original.Hours
          ? resultTime
          : "___";
      },
    },
    {
      accessorKey: "reason",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          الحالة
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const officialTimeIn = timeWork?.time_in;
        const officialTimeOut = timeWork?.time_out;
        let status = "حاضر";
        let isLate = false;
        let isEarly = false;

        const formatToHours = (timeString) => {
          if (!timeString) return "غير متوفر";

          const date = new Date(timeString);
          return new Intl.DateTimeFormat("ar-us", {
            timeZone: "Africa/Cairo",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          }).format(date);
        };

        if (!item.time_in) {
          status = "غائب";
        } else {
          if (formatToHours(item.time_in) > officialTimeIn) {
            isLate = true;
          }
          if (item.time_out && formatToHours(item.time_out) < officialTimeOut) {
            isEarly = true;
          }
        }

        return (
          <div className="flex items-center gap-2">
            {isLate && getStatusBadge("متأخر")}
            {isEarly && getStatusBadge("مبكر")}
            {!isLate && !isEarly && getStatusBadge(status)}
          </div>
        );
      },
    },
    {
      id: "Actions",
      header: "الإجراءات",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <ReasonsDialog data={row.original} />
          <Button
            size="sm"
            className="rounded-lg px-2 text-green-600"
            variant="ghost"
            onClick={() => {
              setEditRecord(row.original);
              setEditSheetOpen(true);
            }}
          >
            <Edit2 />
          </Button>
          {!loading && user?.role === "admin" && (
            <DialogDelete
              title={"الموعد"}
              apiUrl={`/attendance/${row.original.id}`}
              fetchData={handleFetchAttendance}
            />
          )}
        </div>
      ),
    },
  ];

  const exportData = departureAttendance.map((row) => {
    const diffInSeconds =
      timeStringToSeconds(user?.work_hours) -
      timeStringToSeconds(row.Hours || "00:00:00");
    const Hours_Late =
      user?.work_hours !== "00:00:00" && row.Hours
        ? secondsToTimeString(diffInSeconds)
        : "___";

    return {
      user: row.user_id?.name || "",
      email: row.user_id?.email || "",
      date: row.created_at ? formatDate(row.created_at) : "___",
      time_in: row.time_in ? formatTime(row.time_in) : "___",
      time_out: row.time_out ? formatTime(row.time_out) : "___",
      reason_late: row.reason_late || "",
      reason_early: row.reason_early || "",
      Hours: row.Hours || "",
      Hours_Late: Hours_Late,
      status:
        row.reason_late || row.reason_early
          ? row.reason_late
            ? "متأخر"
            : "مغادرة مبكرة"
          : row.status
          ? "حاضر"
          : "غائب",
    };
  });

  const exportColumns = [
    { accessorKey: "user", header: "الموظف" },
    { accessorKey: "email", header: "البريد الإلكتروني" },
    { accessorKey: "date", header: "التاريخ" },
    { accessorKey: "time_in", header: "الحضور" },
    { accessorKey: "time_out", header: "الانصراف" },
    { accessorKey: "reason_late", header: "سبب الحضور المتأخر" },
    { accessorKey: "reason_early", header: "سبب الانصراف المبكر" },
    { accessorKey: "Hours", header: "ساعات الحضور" },
    { accessorKey: "Hours_Late", header: "مدة التأخر" },
    { accessorKey: "status", header: "الحالة" },
  ];

  const [dateFilter, setDateFilter] = useState(null);

  function filterByCreatedAt(data, date = null) {
    if (!date) {
      return data;
    }

    // return data.filter((item) => {
    //   const createdDate = item.created_at.split("T")[0];
    //   return createdDate === date;
    // });
    return data.filter((item) => item.created_at.includes(date));
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ type: "spring", duration: 2 }}
      dir="rtl"
      className="space-y-5"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-2xl font-bold">الحضور و الانصراف</h1>
        <div className="flex items-center gap-2">
          {!loadingCheckAttendance && !checkAttendance.success && (
            <Button
              onClick={handleAttendanceClick}
              disabled={loadingAttendanceStatus}
            >
              الحضور
              {loadingAttendanceStatus && (
                <div className="animate-spin mb-px">
                  <Loader2 />
                </div>
              )}
            </Button>
          )}
          {checkAttendance?.data && !checkAttendance.data.time_out && (
            <Button onClick={handleDepartureClick} disabled={loadingDeparture}>
              {loadingDeparture && (
                <div className="animate-spin mb-px">
                  <Loader2 />
                </div>
              )}
              الانصراف
            </Button>
          )}
          {!loading && user?.role === "admin" && <SheetTimeWork />}
        </div>
      </div>

      {loadingAttendance || loadingCheckAttendance || loading ? (
        <SkeletonTable />
      ) : (
        <DataTable
          showDateFilter={true}
          columns={columns}
          data={filterByCreatedAt(departureAttendance, dateFilter)}
          title={"الحضور و الانصراف"}
          filterNames={["user_id_name", "Hours"]}
          exportData={exportData}
          exportColumns={exportColumns}
          setDateFilter={setDateFilter}
          useDateFilter={true}
        />
      )}

      {showAttendanceSheet && (
        <Sheet open={showAttendanceSheet} onOpenChange={setShowAttendanceSheet}>
          <SheetContent side="left" dir="rtl">
            <SheetHeader className="text-start">
              <SheetTitle>سبب التأخر</SheetTitle>
              <SheetDescription>
                يرجى إدخال سبب التأخر في الحضور
              </SheetDescription>
            </SheetHeader>
            <textarea
              className="w-full p-2 border rounded mt-3"
              placeholder="أدخل سبب التأخر"
              value={lateReason}
              onChange={(e) => setLateReason(e.target.value)}
            />
            <div className="mt-3 text-end">
              <Button
                onClick={() => submitAttendance(lateReason)}
                disabled={loadingAttendanceStatus}
              >
                {loadingAttendanceStatus ? "جاري الحفظ..." : "حفظ"}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}
      {showDepartureSheet && (
        <Sheet open={showDepartureSheet} onOpenChange={setShowDepartureSheet}>
          <SheetContent side="left" dir="rtl">
            <SheetHeader className="text-start">
              <SheetTitle>سبب الانصراف المبكر</SheetTitle>
              <SheetDescription>
                يرجى إدخال سبب الانصراف المبكر
              </SheetDescription>
            </SheetHeader>
            <textarea
              className="w-full p-2 border rounded mt-3"
              placeholder="أدخل سبب الانصراف المبكر"
              value={earlyReason}
              onChange={(e) => setEarlyReason(e.target.value)}
            />
            <div className="mt-3 text-end">
              <Button
                onClick={() => submitDeparture(earlyReason)}
                disabled={loadingDeparture}
              >
                {loadingDeparture ? "جاري الحفظ..." : "حفظ"}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}
      {editSheetOpen && (
        <Sheet open={editSheetOpen} onOpenChange={setEditSheetOpen}>
          <SheetContent side="left" dir="rtl">
            <SheetHeader className="text-start">
              <SheetTitle>تعديل أسباب التأخر والانصراف المبكر</SheetTitle>
            </SheetHeader>
            <div className="mt-5">
              <label className="block font-medium">سبب التأخر</label>
              <textarea
                className="w-full p-2 border rounded mt-1"
                value={editRecord.reason_late || ""}
                onChange={(e) =>
                  setEditRecord({ ...editRecord, reason_late: e.target.value })
                }
                placeholder="أدخل سبب التأخر"
              />
            </div>
            <div className="mt-3 ">
              <label className="block font-medium">سبب الانصراف المبكر</label>
              <textarea
                className="w-full p-2 border rounded mt-1"
                value={editRecord.reason_early || ""}
                onChange={(e) =>
                  setEditRecord({ ...editRecord, reason_early: e.target.value })
                }
                placeholder="أدخل سبب الانصراف المبكر"
              />
            </div>
            <div className="mt-3 text-end">
              <Button onClick={handleEditReasons} disabled={loadingEdit}>
                {loadingEdit && <Loader2 className="animate-spin" />}
                حفظ التعديل
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </motion.div>
  );
}

const SheetTimeWork = () => {
  const [loading, setLoading] = useState(false);
  const [loadingFetch, setLoadingFetch] = useState(false);
  const [errors, setErrors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    time_in: "09:00:00",
    time_out: "05:00:00",
  });

  const handleFetchTimeWork = async () => {
    try {
      setLoadingFetch(true);
      setErrors([]);
      const { data } = await axios.get(`${BaseUrlApi}/attendance/timeWork`);
      setForm({
        time_in: data.data.time_in,
        time_out: data.data.time_out,
      });
    } catch (error) {
      setErrors(ErrorMessage(error));
    } finally {
      setLoadingFetch(false);
    }
  };

  useEffect(() => {
    handleFetchTimeWork();
  }, []);

  const handleCreateTimeWork = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setErrors([]);
      await axios.post(`${BaseUrlApi}/attendance/timeWork`, form);
      toast("تم إنشاء وقت العمل بنجاح", {
        description: "تمت إضافة وقت العمل الجديد بنجاح 🎉",
      });
      setOpen(false);
    } catch (error) {
      setErrors(ErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button disabled={loadingFetch} variant="outline">
          تعديل وقت العمل
        </Button>
      </SheetTrigger>
      <SheetContent side={"left"} dir="rtl">
        <SheetHeader className="text-start">
          <SheetTitle>وقت العمل</SheetTitle>
          <SheetDescription>🕘 اختر اوقات العمل الرسمية</SheetDescription>
        </SheetHeader>
        <form className="grid gap-2 mt-5" onSubmit={handleCreateTimeWork}>
          <label htmlFor="time_in" className="font-medium -mb-1">
            بداية الوقت
          </label>
          <TimePicker
            onChange={(time, timeString) =>
              setForm({ ...form, time_in: timeString })
            }
            defaultValue={dayjs(form.time_in, "HH:mm:ss")}
          />
          {errors?.time_in && (
            <p className="text-red-500 font-semibold text-sm">
              {errors.time_in[0]}
            </p>
          )}
          <label htmlFor="time_out" className="font-medium -mb-1">
            نهاية الوقت
          </label>
          <TimePicker
            onChange={(time, timeString) =>
              setForm({ ...form, time_out: timeString })
            }
            defaultValue={dayjs(form.time_out, "HH:mm:ss")}
          />
          {errors?.time_out && (
            <p className="text-red-500 font-semibold text-sm">
              {errors.time_out[0]}
            </p>
          )}
          <div className="text-end">
            <Button disabled={loading}>
              {loading ? "جاري الحفظ..." : "حفظ"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export function ReasonsDialog({ data }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="rounded-lg px-2 border-none text-yellow-600"
        >
          <Eye />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle className="text-right">تفاصيل الأسباب</DialogTitle>
        </DialogHeader>
        <div className="mt-5">
          <label className="block font-medium">سبب التأخر</label>
          <textarea
            className="w-full p-2 border rounded mt-1"
            value={data.reason_late}
            readOnly
            placeholder="أدخل سبب التأخر"
          />
        </div>
        <div className="mt-3">
          <label className="block font-medium">سبب الانصراف المبكر</label>
          <textarea
            className="w-full p-2 border rounded mt-1"
            value={data.reason_early}
            readOnly
            placeholder="أدخل سبب الانصراف المبكر"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
